import ApplicationActionTypes from 'redux/applications/types';
import request from 'utils/request';
import urls from 'utils/config';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import moment from 'moment';
 
import {
  createApplicationSuccess,
  createApplicationFailure,
  editApplicationFailure,
  editApplicationSuccess,
  fetchApplicationsSuccess,
  fetchApplicationsFailure,
  fetchCoursesFailure,
  fetchCoursesSuccess,
  searchResult,
  deleteApplicationSuccess,
  fetchSingleApplicationSuccess,
  fetchSingleApplicationFailure,
  searchCandidateGroupSuccess,
  searchCandidateGroupFailure,
  fetchSingleGroupSuccess,
  fetchSingleGroupFailure,
} from 'redux/applications/actions';
import { startFetchTiedApplications } from 'redux/forms/actions';
import { TOP_RIGHT, SUCCESS } from 'utils/constants';
import { Notify } from '@flexisaf/flexibull2';
import { statusCodes } from 'utils/constants';
import { networkErrorHandler } from 'utils/errrorHandlers';

const { API_BASE_URL } = urls || {};

function* createApplication({ payload: { application, cb } }) {
  const url = `${API_BASE_URL}/applications`;

  const response = yield request({
    method: 'post',
    data: application,
    url,
  });
  if (response.success) {
    yield put(createApplicationSuccess(response.raw));
    yield put(startFetchTiedApplications());
    Notify('Application created', {
      position: TOP_RIGHT,
      status: SUCCESS,
    });
    cb(response.raw.id);
  } else {
    yield put(
      createApplicationFailure(
        'An error occurred. Please reload the page to try again.'
      )
    );

    if (response.statusCode === statusCodes.CONFLICT) {
      networkErrorHandler(response?.statusCode, 'Application already exists');
    } else if (response.message) {
      networkErrorHandler(response?.statusCode, response.message);
    } else {
      networkErrorHandler(
        response?.statusCode,
        'An error occurred. Please reload the page to try again.'
      );
    }
  }
}

function* editApplication({ payload: { application, cb } }) {
  const url = `${API_BASE_URL}/applications/${application.id}`;

  const response = yield request({
    method: 'put',
    data: application,
    url,
  });
  if (response.success) {
    yield put(editApplicationSuccess(response.raw));
    yield put(startFetchTiedApplications());
    Notify('Application updated', {
      position: TOP_RIGHT,
      status: SUCCESS,
    });
    cb(response.raw.id);
  } else {
    yield put(
      editApplicationFailure(
        'An error occured. Please reload the page to try again.'
      )
    );
    networkErrorHandler(response?.statusCode, response?.message);
  }
}

function* fetchApplications({ payload: { currentPage, pageSize } }) {
  const safePageSize = pageSize ?? 10;
  const pageNumber = currentPage ?? 0;
  const url = `${API_BASE_URL}/applications?page=${pageNumber}&size=${safePageSize}`;

  const response = yield request({
    method: 'get',
    url,
  });
  if (response.success) {
    yield put(fetchApplicationsSuccess(response.raw));
  } else {
    yield put(
      fetchApplicationsFailure(
        'An error occured. Please reload the page to try again.'
      )
    );
    networkErrorHandler(response?.statusCode, response?.message);
  }
}

function* fetchSingleApplication({ payload: id }) {
  const url = `${API_BASE_URL}/applications/${id}`;
  const response = yield request({
    method: 'get',
    url,
  });

  if (response.success) {
    yield put(fetchSingleApplicationSuccess(response.raw));
  } else {
    networkErrorHandler(
      response?.statusCode,
      response?.message || 'Unable to Fetch Application'
    );
    yield put(fetchSingleApplicationFailure(response.message));
  }
}

function* deleteApplication({ payload }) {
  const url = `${API_BASE_URL}/applications/${payload}`;

  const response = yield request({
    method: 'delete',
    url,
  });
  if (response.success) {
    yield put(deleteApplicationSuccess(payload));
    Notify('Application Deleted Successfully', {
      position: TOP_RIGHT,
      status: SUCCESS,
    });
  } else {
    yield put({ type: ApplicationActionTypes.DELETE_APPLICATION_FAIL });
    if (response.statusCode === statusCodes.BAD_REQUEST) {
      networkErrorHandler(
        response?.statusCode,
        response?.message || 'Application is currently in use'
      );
    } else {
      networkErrorHandler(response?.statusCode, response?.message);
    }
  }
}

function* searchCandidateGroupsForApplication({ payload: { name, formId } }) {
  const url = `${API_BASE_URL}/candidate-groups?name=${name}&schemaId=${formId}`;

  const response = yield request({
    method: 'get',
    url,
  });
  if (response.success) {
    yield put(searchCandidateGroupSuccess(response.raw.data));
  } else {
    yield put(searchCandidateGroupFailure());
  }
}

function* fetchSingleGroup({ payload: names }) {
  try {
    const response = yield Promise.all(
      names.map((name) => {
        const url = `${API_BASE_URL}/candidate-groups/${name}`;
        return request({
          method: 'get',
          url,
        });
      })
    );

    if (response[0].success) {
      yield put(fetchSingleGroupSuccess(response.map((r) => r.raw)));
    } else {
      yield put(fetchSingleGroupFailure());
    }
  } catch (e) {
    yield put(fetchSingleGroupFailure());
  }
}

function* onDeleteApplication() {
  yield takeLatest(
    ApplicationActionTypes.DELETE_APPLICATION_START,
    deleteApplication
  );
}

function* fetchCourses({ payload: { organisation } }) {
  const url = `${API_BASE_URL}/courses?organisationId=${organisation}`;

  const response = yield request({
    method: 'get',
    url,
  });
  if (response.success) {
    yield put(fetchCoursesSuccess(response.raw));
  } else {
    yield put(
      fetchCoursesFailure(
        'An error occured. Please reload the page to try again.'
      )
    );
    networkErrorHandler(response?.statusCode, response?.message);
  }
}

function* searchApplications({ data }) {
  const { searchPattern, pageSize } = data;
  const url = `${API_BASE_URL}/applications/search?pageSize=${
    pageSize || 10
  }&keyword=${searchPattern}`;

  const response = yield request({
    method: 'get',
    url,
  });
  if (response.success) {
    yield put(searchResult(response.raw));
  } else {
    yield put(fetchCoursesFailure(response.message));
  }
}
export function* filterApplications({ data }) {
  const { groups, published, status, timeFrame, cb } = data;
  const startDate = timeFrame
    ? moment(timeFrame?.startDate).format('YYYY-MM-DD')
    : null;
  const endDate = timeFrame
    ? moment(timeFrame?.endDate).format('YYYY-MM-DD')
    : null;
  const groupItemIds =
    groups &&
    groups
      .map((groupId) => `&groupItemId=${groupId.value}`)
      .toString()
      .replace(/,/g, '');
  const url = `${API_BASE_URL}/applications/search?pageSize=10${
    groups ? `${groupItemIds}` : ''
  }${published ? `&active=${published}` : ''}${
    status ? `&status=${status}` : ''
  }${timeFrame ? `&startDate=${startDate}&endDate=${endDate}` : ''}`;

  const response = yield request({
    method: 'get',
    url,
  });
  if (response.success) {
    yield put(searchResult(response.raw));
    if (cb && typeof cb === 'function') {
      cb();
    }
  } else {
    yield put(fetchCoursesFailure(response.message));
  }
}

function* onCreateApplication() {
  yield takeLatest(
    ApplicationActionTypes.START_CREATE_APPLICATION,
    createApplication
  );
}

function* onEditApplication() {
  yield takeLatest(
    ApplicationActionTypes.START_EDIT_APPLICATION,
    editApplication
  );
}

function* onFetchApplications() {
  yield takeLatest(
    ApplicationActionTypes.START_FETCH_APPLICATIONS,
    fetchApplications
  );
}
function* onFetchSingleApplication() {
  yield takeLatest(
    ApplicationActionTypes.START_FETCH_SINGLE_APPLICATIONS,
    fetchSingleApplication
  );
}

function* onFetchCourses() {
  yield takeLatest(ApplicationActionTypes.START_FETCH_COURSES, fetchCourses);
}

function* onSearchApplication() {
  yield takeLatest(
    ApplicationActionTypes.START_APPLICATIONS_SEARCH,
    searchApplications
  );
}
function* onFilter() {
  yield takeLatest(
    ApplicationActionTypes.FILTER_APPLICATIONS,
    filterApplications
  );
}
function* onSearchCandidateGroup() {
  yield takeLatest(
    ApplicationActionTypes.SEARCH_CANDIDATE_GROUPS,
    searchCandidateGroupsForApplication
  )
}
function* onFetchSingleGroup() {
  yield takeLatest(
    ApplicationActionTypes.FETCH_SINGLE_GROUP,
    fetchSingleGroup
  );
}

export function* applicationSagas() {
  yield all([
    call(onCreateApplication),
    call(onEditApplication),
    call(onFetchApplications),
    call(onFetchSingleApplication),
    call(onFetchCourses),
    call(onDeleteApplication),
    call(onSearchApplication),
    call(onFilter),
    call(onSearchCandidateGroup),
    call(onFetchSingleGroup),
  ]);
}
